import { create } from "zustand";
import { API } from "@/lib/apiConfig";

const API_BASE = API.subsidyEligibility;

/**
 * EU Cross-Sovereign Subsidy Eligibility Engine Store (v1)
 * 
 * Manages subsidy eligibility scoring, grant reduction calculations,
 * and escalation path determination for cross-sovereign subsidies.
 */
export interface SubsidyEligibilityStoreState {
  tribunalDecision: "pending" | "warning" | "fine" | "disqualification";
  chainIntegrityScore: number; // 0-100
  meshIntegrity: number; // 0-100
  passportLevel: "basic" | "enhanced" | "sovereign";
  corruptionSignals: number;
  geopoliticalWeight: number; // 0-5
  subsidyEligibilityScore: number; // 0-100
  grantReductionPercent: number;
  riskOfMisuseFlag: boolean;
  escalationPath: "none" | "regionalAudit" | "euAudit" | "prosecution";

  // Actions
  setTribunalDecision: (decision: "pending" | "warning" | "fine" | "disqualification") => void;
  setChainIntegrityScore: (score: number) => void;
  setMeshIntegrity: (integrity: number) => void;
  setPassportLevel: (level: "basic" | "enhanced" | "sovereign") => void;
  setCorruptionSignals: (signals: number) => void;
  setGeopoliticalWeight: (weight: number) => void;

  // Derived outputs
  getBaseScore: () => number;
  getPassportBonus: () => number;
  getCorruptionPenalty: () => number;
  getGeoPenalty: () => number;
  getTribunalPenalty: () => number;
  getSubsidyEligibilityScore: () => number;
  getGrantReductionPercent: () => number;
  getRiskOfMisuseFlag: () => boolean;
  getEscalationPath: () => "none" | "regionalAudit" | "euAudit" | "prosecution";

  // Actions
  computeEligibility: () => Promise<void>;

  // Export/Import
  exportEligibilityEnvelope: () => void;
  importEligibilityEnvelope: (data: {
    tribunalDecision?: "pending" | "warning" | "fine" | "disqualification";
    chainIntegrityScore?: number;
    meshIntegrity?: number;
    passportLevel?: "basic" | "enhanced" | "sovereign";
    corruptionSignals?: number;
    geopoliticalWeight?: number;
  }) => void;
}

export const useSubsidyEligibilityStore = create<SubsidyEligibilityStoreState>((set, get) => ({
  tribunalDecision: "pending",
  chainIntegrityScore: 100,
  meshIntegrity: 100,
  passportLevel: "basic",
  corruptionSignals: 0,
  geopoliticalWeight: 0,
  subsidyEligibilityScore: 100,
  grantReductionPercent: 0,
  riskOfMisuseFlag: false,
  escalationPath: "none",

  setTribunalDecision: (decision) => set({ tribunalDecision: decision }),
  setChainIntegrityScore: (score) => set({ chainIntegrityScore: Math.max(0, Math.min(100, score)) }),
  setMeshIntegrity: (integrity) => set({ meshIntegrity: Math.max(0, Math.min(100, integrity)) }),
  setPassportLevel: (level) => set({ passportLevel: level }),
  setCorruptionSignals: (signals) => set({ corruptionSignals: Math.max(0, signals) }),
  setGeopoliticalWeight: (weight) => set({ geopoliticalWeight: Math.max(0, Math.min(5, weight)) }),

  getBaseScore: () => {
    const state = get();
    return (state.chainIntegrityScore * 0.4) + (state.meshIntegrity * 0.4);
  },

  getPassportBonus: () => {
    const state = get();
    if (state.passportLevel === "sovereign") return 10;
    if (state.passportLevel === "enhanced") return 5;
    return 0; // basic
  },

  getCorruptionPenalty: () => {
    return get().corruptionSignals * 4;
  },

  getGeoPenalty: () => {
    return get().geopoliticalWeight * 2;
  },

  getTribunalPenalty: () => {
    const state = get();
    if (state.tribunalDecision === "disqualification") return -25;
    if (state.tribunalDecision === "fine") return -10;
    if (state.tribunalDecision === "warning") return -3;
    return 0; // pending
  },

  getSubsidyEligibilityScore: () => {
    const state = get();
    const baseScore = state.getBaseScore();
    const passportBonus = state.getPassportBonus();
    const corruptionPenalty = state.getCorruptionPenalty();
    const geoPenalty = state.getGeoPenalty();
    const tribunalPenalty = state.getTribunalPenalty();

    const score = baseScore + passportBonus - corruptionPenalty - geoPenalty + tribunalPenalty;
    return Math.max(0, Math.min(100, score));
  },

  getGrantReductionPercent: () => {
    const state = get();
    const score = state.getSubsidyEligibilityScore();
    const reduction = 100 - score;
    return Math.max(0, Math.min(80, reduction));
  },

  getRiskOfMisuseFlag: () => {
    const state = get();
    return state.getSubsidyEligibilityScore() < 50;
  },

  getEscalationPath: () => {
    const state = get();
    const score = state.getSubsidyEligibilityScore();
    if (score >= 70) return "none";
    if (score >= 50) return "regionalAudit";
    if (score >= 30) return "euAudit";
    return "prosecution";
  },

  computeEligibility: async () => {
    const state = get();
    
    // Try to sync with backend if available
    try {
      const response = await fetch(`${API_BASE}/compute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tribunalDecision: state.tribunalDecision,
          chainIntegrityScore: state.chainIntegrityScore,
          meshIntegrity: state.meshIntegrity,
          passportLevel: state.passportLevel,
          corruptionSignals: state.corruptionSignals,
          geopoliticalWeight: state.geopoliticalWeight,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update state from backend response
        set({
          subsidyEligibilityScore: data.state.subsidyEligibilityScore,
          grantReductionPercent: data.state.grantReductionPercent,
          riskOfMisuseFlag: data.state.riskOfMisuseFlag,
          escalationPath: data.state.escalationPath,
        });
        return;
      }
    } catch (error) {
      console.log("Backend not available, using local computation only");
    }
    
    // Fallback to local computation
    const subsidyScore = state.getSubsidyEligibilityScore();
    const grantReduction = state.getGrantReductionPercent();
    const riskFlag = state.getRiskOfMisuseFlag();
    const escalation = state.getEscalationPath();

    set({
      subsidyEligibilityScore: subsidyScore,
      grantReductionPercent: grantReduction,
      riskOfMisuseFlag: riskFlag,
      escalationPath: escalation,
    });
  },

  exportEligibilityEnvelope: () => {
    const state = get();
    const report = {
      subsidyEligibilityScore: state.getSubsidyEligibilityScore(),
      grantReductionPercent: state.getGrantReductionPercent(),
      riskOfMisuseFlag: state.getRiskOfMisuseFlag(),
      escalationPath: state.getEscalationPath(),
      tribunalDecision: state.tribunalDecision,
      corruptionSignals: state.corruptionSignals,
      geopoliticalWeight: state.geopoliticalWeight,
      passportLevel: state.passportLevel,
      chainIntegrityScore: state.chainIntegrityScore,
      meshIntegrity: state.meshIntegrity,
      baseScore: state.getBaseScore(),
      passportBonus: state.getPassportBonus(),
      corruptionPenalty: state.getCorruptionPenalty(),
      geoPenalty: state.getGeoPenalty(),
      tribunalPenalty: state.getTribunalPenalty(),
    };

    const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subsidyEligibilityEnvelope_${dateStr}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  importEligibilityEnvelope: (data) => {
    set((state) => ({
      ...state,
      tribunalDecision: data.tribunalDecision ?? state.tribunalDecision,
      chainIntegrityScore: data.chainIntegrityScore ?? state.chainIntegrityScore,
      meshIntegrity: data.meshIntegrity ?? state.meshIntegrity,
      passportLevel: data.passportLevel ?? state.passportLevel,
      corruptionSignals: data.corruptionSignals ?? state.corruptionSignals,
      geopoliticalWeight: data.geopoliticalWeight ?? state.geopoliticalWeight,
    }));
    // Recompute after import
    get().computeEligibility();
  },
}));






